import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, CheckCircle2, XCircle, Loader2, AlertTriangle, FileText, ChevronLeft, Wand2, ShieldCheck, Eye, Trash2, Languages } from 'lucide-react';
import { authFetch } from '../lib/authFetch';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';

const SUPPORTED_LANGS = ['en', 'it', 'fr', 'es', 'ru', 'uk', 'pt', 'ko', 'zh', 'zh-TW'];


/**
 * ProgrammeUploadChecklist
 * ------------------------
 * 30-row matrix showing which Programme Week files are present.  Two columns
 * per row: Reading (`ARP_Program_W{NN}_EN`) and Self-Work
 * (`ARP_Program_W{NN}_SW_EN`).  Cells turn green as soon as content of at
 * least 80 chars exists for that slot.  Also shows the Personal History
 * (2WW) row at the top so the SuperAdmin can see the full content tree at
 * a glance.
 */
function ProgrammeUploadChecklist({ coverage, onBulkTranslate, bulkTranslateState }) {
  if (!coverage?.rows) return null;
  const rows = coverage.rows;

  // Personal History (2WW) — the two intro weeks PLUS Disease + Daily Structure.
  const ph = rows.filter(r =>
    r.section === 'programme' &&
    (r.key === 'disease-module' || r.key === 'daily-structure' ||
     r.key === 'intro-2ww-w1' || r.key === 'intro-2ww-w2')
  );

  // Programme Weeks 1..30 — paired with their SW row.
  const progByPw = {};
  rows.forEach(r => {
    if (r.section === 'programme' && typeof r.programme_week === 'number') {
      progByPw[r.programme_week] = progByPw[r.programme_week] || {};
      progByPw[r.programme_week].reading = r;
    }
    if (r.section === 'sw' && typeof r.programme_week === 'number') {
      progByPw[r.programme_week] = progByPw[r.programme_week] || {};
      progByPw[r.programme_week].sw = r;
    }
  });

  const filledCount = Object.values(progByPw).filter(p => p.reading?.languages?.en === 'green').length;
  const swFilledCount = Object.values(progByPw).filter(p => p.sw?.languages?.en === 'green').length;

  const Cell = ({ state, missingLabel }) => {
    const cls = state === 'green'
      ? 'bg-emerald-100 border-emerald-300 text-emerald-800'
      : state === 'amber'
        ? 'bg-amber-100 border-amber-300 text-amber-800'
        : 'bg-zinc-50 border-zinc-200 text-zinc-400';
    const Icon = state === 'green' ? CheckCircle2 : state === 'amber' ? AlertTriangle : XCircle;
    return (
      <div className={`flex items-center justify-center gap-1.5 px-2 py-1 rounded border ${cls} text-[10px] font-mono`}>
        <Icon className="w-3 h-3 flex-shrink-0" />
        <span className="truncate">{state === 'green' ? 'OK' : state === 'amber' ? 'AMBER' : missingLabel}</span>
      </div>
    );
  };

  return (
    <div className="mt-6 bg-white border border-zinc-200 rounded-xl overflow-hidden" data-testid="arp-upload-checklist">
      <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-sm font-bold text-zinc-900">Upload checklist  ·  30 Programme Weeks</h2>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            Drag the files matching the expected filenames into the dropzone above.  Rows light up green as files land.
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs flex-wrap">
          <div className="flex items-center gap-1.5" data-testid="arp-checklist-reading-count">
            <span className="font-bold text-emerald-700">{filledCount}</span>
            <span className="text-zinc-500">/ 30 Readings</span>
          </div>
          <div className="flex items-center gap-1.5" data-testid="arp-checklist-sw-count">
            <span className="font-bold text-emerald-700">{swFilledCount}</span>
            <span className="text-zinc-500">/ 30 Self-Works</span>
          </div>
          <button
            onClick={onBulkTranslate}
            disabled={bulkTranslateState?.running}
            className="px-3 py-1.5 rounded-md bg-blue-600 text-white text-[11px] font-bold uppercase tracking-wider hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
            data-testid="arp-bulk-translate-btn"
          >
            {bulkTranslateState?.running ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" />
                <span>Translating  ·  {bulkTranslateState.done}/{bulkTranslateState.total}</span>
              </>
            ) : (
              <>
                <Languages className="w-3 h-3" />
                <span>Translate all missing (9 languages)</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Personal History band */}
      {ph.length > 0 && (
        <div className="px-4 py-3 bg-amber-50 border-b border-amber-200">
          <p className="text-[10px] uppercase tracking-wider font-bold text-amber-800 mb-2">Personal History  ·  2 Week Window  (assessment period, no Self-Work)</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
            {ph.map(r => (
              <div key={r.key} className="flex items-center gap-2 text-[11px]" data-testid={`ph-row-${r.key}`}>
                <span className="text-zinc-700 flex-1 truncate">{r.label}</span>
                <Cell state={r.languages?.en} missingLabel="MISSING" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 30-row Programme grid */}
      <div className="px-4 py-3">
        <div className="grid grid-cols-[auto_1fr_1fr] gap-x-3 gap-y-1.5 text-[11px]">
          <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">Week</div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">Reading  ·  ARP_Program_WNN_EN</div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">Self-Work  ·  ARP_Program_WNN_SW_EN</div>
          {Array.from({ length: 30 }, (_, i) => i + 1).map(pw => {
            const pair = progByPw[pw] || {};
            const readState = pair.reading?.languages?.en;
            const swState = pair.sw?.languages?.en;
            return (
              <React.Fragment key={pw}>
                <div className="flex items-center" data-testid={`checklist-row-pw${pw}`}>
                  <span className={`inline-flex items-center justify-center w-7 h-6 rounded font-bold text-[11px] ${
                    readState === 'green' && swState === 'green'
                      ? 'bg-emerald-600 text-white'
                      : readState === 'green' || swState === 'green'
                        ? 'bg-zinc-200 text-zinc-800'
                        : 'bg-zinc-100 text-zinc-500'
                  }`}>
                    {pw < 10 ? `W0${pw}` : `W${pw}`}
                  </span>
                </div>
                <Cell state={readState} missingLabel={`ARP_Program_W${pw < 10 ? '0' + pw : pw}_EN`} />
                <Cell state={swState} missingLabel={`ARP_Program_W${pw < 10 ? '0' + pw : pw}_SW_EN`} />
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function ARPUploadsPage() {
  const navigate = useNavigate();
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadQueue, setUploadQueue] = useState([]);   // [{name,size,status,result?,error?}]
  const [dragOver, setDragOver] = useState(false);
  const [filterLang, setFilterLang] = useState('all');
  const [translating, setTranslating] = useState({});       // `${key}-${lang}` -> true while in flight
  const [auditOpen, setAuditOpen] = useState(null);         // { row, lang, audit }
  const [deleting, setDeleting] = useState({});              // `${key}` or `${key}-${lang}` -> true while in flight
  const [confirmDelete, setConfirmDelete] = useState(null);  // { row, language } | null
  const [bulkTranslateState, setBulkTranslateState] = useState({ running: false, done: 0, total: 0 });

  const fetchCoverage = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/arp-coverage`);
      if (r.ok) setCoverage(await r.json());
    } catch { /* noop */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchCoverage(); }, [fetchCoverage]);

  /**
   * Schedule auto-translation for EVERY missing non-EN slot across every
   * reading + lesson row.  The backend returns a list of task_ids; we poll
   * each one with bounded concurrency and refresh the coverage matrix once
   * all have finished.
   */
  const handleBulkTranslate = useCallback(async () => {
    if (bulkTranslateState.running) return;
    try {
      const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/arp-translate/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),  // defaults: every non-EN lang, both sections
      });
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || 'Could not start bulk translation.');
        return;
      }
      const tasks = d.tasks || [];
      if (tasks.length === 0) {
        toast.success('Nothing to translate: every cell is already filled in.');
        return;
      }
      toast.info(`Translating ${tasks.length} cells across ${(d.target_langs || []).length} languages.  This may take several minutes.`);
      setBulkTranslateState({ running: true, done: 0, total: tasks.length });

      // Poll each task with mild concurrency (sequential polls, but tasks
      // run in parallel on the server side).
      let done = 0;
      const pollOne = async (taskId) => {
        for (let i = 0; i < 60; i += 1) {       // up to ~5 min per task
          await new Promise(r => setTimeout(r, 5000));
          try {
            const sr = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/arp-translate/status/${taskId}`);
            if (sr.ok) {
              const sj = await sr.json();
              if (sj.status === 'done' || sj.status === 'failed') return sj.status;
            }
          } catch { /* keep polling */ }
        }
        return 'timeout';
      };
      // Poll up to 6 tasks at a time.
      const queue = [...tasks];
      const workers = Array.from({ length: 6 }, async () => {
        while (queue.length) {
          const t = queue.shift();
          if (!t) break;
          await pollOne(t.task_id);
          done += 1;
          setBulkTranslateState(s => ({ ...s, done }));
        }
      });
      await Promise.all(workers);
      setBulkTranslateState({ running: false, done: 0, total: 0 });
      toast.success(`Bulk translation complete (${tasks.length} cells).  Review the amber cells before publishing.`);
      fetchCoverage();
    } catch (e) {
      setBulkTranslateState({ running: false, done: 0, total: 0 });
      toast.error('Bulk translation failed.');
    }
  }, [bulkTranslateState.running, fetchCoverage]);

  const deleteRow = useCallback((row, language = null) => {
    // Open an in-app confirmation modal.  Native window.confirm() is silently
    // blocked by Chrome when the app is embedded in the Emergent preview
    // iframe, so we use a stateful modal instead.
    setConfirmDelete({ row, language });
  }, []);

  const performDelete = useCallback(async () => {
    if (!confirmDelete) return;
    const { row, language } = confirmDelete;
    setConfirmDelete(null);
    const flightKey = language ? `${row.key}-${language}` : row.key;
    setDeleting(d => ({ ...d, [flightKey]: true }));
    try {
      const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/arp-content`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          section: row.section,
          key: row.key,
          language: language || null,
          confirmed: true,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok) {
        toast.success(`Deleted${language && language !== 'all' ? ` ${language.toUpperCase()}` : ''} ${row.label}`);
        fetchCoverage();
      } else {
        toast.error(data.detail || 'Delete failed');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setDeleting(d => { const n = { ...d }; delete n[flightKey]; return n; });
    }
  }, [confirmDelete, fetchCoverage]);


  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/arp-upload`, {
      method: 'POST', body: fd,
    });
    const d = await r.json().catch(() => ({}));
    return { ok: r.ok, status: r.status, data: d };
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList);
    if (!files.length) return;

    const initial = files.map(f => ({ name: f.name, size: f.size, status: 'queued' }));
    setUploadQueue(prev => [...prev, ...initial]);

    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      setUploadQueue(prev => prev.map(q => q.name === f.name ? { ...q, status: 'uploading' } : q));
      try {
        const res = await uploadOne(f);
        if (res.ok) {
          setUploadQueue(prev => prev.map(q => q.name === f.name ? { ...q, status: 'done', result: res.data } : q));
        } else {
          setUploadQueue(prev => prev.map(q => q.name === f.name ? { ...q, status: 'error', error: res.data?.detail || `HTTP ${res.status}` } : q));
        }
      } catch (e) {
        setUploadQueue(prev => prev.map(q => q.name === f.name ? { ...q, status: 'error', error: String(e) } : q));
      }
    }
    fetchCoverage();   // refresh matrix once the batch finishes
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const onPick = (e) => handleFiles(e.target.files);

  // Auto-translate one row from EN to a target language with safeguards.
  // The backend runs the actual LLM call as a background task; we POST to
  // schedule it (returns ~instantly with a task_id) and then poll the status
  // endpoint every 3 seconds until the task completes.  This keeps the
  // single uvicorn worker free for other requests while a translation runs.
  const autoTranslateOne = async (row, lang) => {
    if (row.section === 'sw' || row.languages.en !== 'green') return;
    const k = `${row.key}-${lang}`;
    setTranslating(prev => ({ ...prev, [k]: true }));
    try {
      // 1.  Schedule the background task
      const scheduleResp = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/arp-translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ section: row.section, key: row.key, target_lang: lang }),
      });
      const scheduleData = await scheduleResp.json().catch(() => ({}));
      if (!scheduleResp.ok || !scheduleData.task_id) {
        toast.error(scheduleData.detail || `Translate failed to start (${scheduleResp.status})`);
        return;
      }
      const taskId = scheduleData.task_id;

      // 2.  Poll for completion (max 6 minutes; long docs = ~3-5 chunks of Claude time)
      let d = null;
      const startTime = Date.now();
      const MAX_WAIT_MS = 6 * 60 * 1000;
      const POLL_INTERVAL_MS = 3000;
      while (Date.now() - startTime < MAX_WAIT_MS) {
        await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
        const statusResp = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/arp-translate/status/${taskId}`);
        if (!statusResp.ok) continue;  // transient
        const sd = await statusResp.json();
        if (sd.status === 'running') continue;
        if (sd.status === 'failed') {
          toast.error(`Translation worker failed: ${sd.error || 'unknown error'}`);
          return;
        }
        // status === 'done'
        d = sd.result || {};
        break;
      }
      if (!d) {
        toast.error('Translation timed out after 6 minutes.  Check the audit log.');
        return;
      }

      // 3.  React to the saved result
      if (!d.ok || !d.saved) {
        toast.error('Translator returned no usable output.  See warnings.');
        setAuditOpen({ row, lang, audit: { warnings: d.warnings || [], integrity: false, ratio: d.ratio, chunks: d.chunks, attempts: d.attempts, audit_id: d.audit_id } });
      } else if (!d.integrity) {
        toast.message(`Translated with ${(d.warnings || []).length} warning(s).  Review before verifying.`);
        setAuditOpen({ row, lang, audit: d });
      } else {
        toast.success(`Translated to ${lang.toUpperCase()}.  All 7 checks passed.`);
      }
      fetchCoverage();
    } catch (e) {
      toast.error(String(e));
    } finally {
      setTranslating(prev => ({ ...prev, [k]: false }));
    }
  };

  // SuperAdmin flips amber -> green after manually reviewing the translation.
  const verifyOne = async (row, lang) => {
    const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/arp-translate/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ section: row.section, key: row.key, target_lang: lang }),
    });
    if (r.ok) { toast.success(`${lang.toUpperCase()} verified.`); fetchCoverage(); }
    else toast.error('Verify failed');
  };

  // Auto-translate one row from EN to ALL empty non-EN languages.
  const autoTranslateRow = async (row) => {
    if (row.section === 'sw' || row.languages.en !== 'green') return;
    const targets = SUPPORTED_LANGS.filter(l => l !== 'en' && row.languages[l] === 'empty');
    if (!targets.length) { toast.message('All non-EN languages are already filled.'); return; }
    toast.message(`Scheduling ${targets.length} translations.  Each runs ~30-60s in the background.  You can keep using the app.`);
    for (const l of targets) {
      // Sequential awaits are intentional — the LLM is rate-limited and
      // parallelising hits a 429.  Don't change to Promise.all without
      // adding a concurrency limiter.
      await autoTranslateOne(row, l);
    }
  };

  const visibleLangs = filterLang === 'all' ? SUPPORTED_LANGS : [filterLang];

  return (
    <div className="min-h-screen bg-zinc-100 pb-24">
      <PageHeader title="ARP Uploads" />
      <main className="pt-20 px-4">
        <div className="max-w-5xl mx-auto">
          <button
            onClick={() => navigate('/profile')}
            className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800 mb-3"
            data-testid="arp-back-btn"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Back to Profile
          </button>

          {/* Drop zone */}
          <div
            onDrop={onDrop}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            className={`border-2 border-dashed rounded-2xl p-8 text-center transition-colors ${
              dragOver ? 'border-blue-500 bg-blue-50' : 'border-zinc-300 bg-white hover:bg-zinc-50'
            }`}
            data-testid="arp-dropzone"
          >
            <Upload className="w-10 h-10 mx-auto text-zinc-400 mb-3" />
            <p className="text-sm font-bold text-zinc-900">Drop ARP_* files here</p>
            <p className="text-[11px] text-zinc-500 mt-1">
              .pdf, .docx, .txt or .md  ·  e.g.  ARP_Lesson_03_EN.pdf, ARP_Lesson03_EN.pdf, ARP_Program_W7_RU.docx
            </p>
            <label className="inline-block mt-3 px-3 py-1.5 rounded-lg bg-zinc-950 text-white text-xs font-bold uppercase tracking-wider cursor-pointer hover:bg-zinc-800" data-testid="arp-file-pick">
              Choose files
              <input type="file" multiple accept=".pdf,.docx,.txt,.md" className="hidden" onChange={onPick} />
            </label>
          </div>

          <ProgrammeUploadChecklist
            coverage={coverage}
            onBulkTranslate={handleBulkTranslate}
            bulkTranslateState={bulkTranslateState}
          />

          {/* Upload queue */}
          {uploadQueue.length > 0 && (
            <div className="mt-4 bg-white border border-zinc-200 rounded-xl divide-y divide-zinc-100" data-testid="arp-queue">
              {uploadQueue.map((q, i) => (
                <div key={`${q.name}-${i}`} className="px-3 py-2 flex items-center gap-3 text-xs">
                  {q.status === 'done' ? <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    : q.status === 'error' ? <XCircle className="w-4 h-4 text-rose-600 flex-shrink-0" />
                    : q.status === 'uploading' ? <Loader2 className="w-4 h-4 text-blue-600 animate-spin flex-shrink-0" />
                    : <FileText className="w-4 h-4 text-zinc-400 flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-mono truncate">{q.name}</p>
                    {q.status === 'done' && q.result && (
                      <p className="text-[10px] text-zinc-500">
                        {q.result.parsed?.kind}  ·  Week {q.result.parsed?.week}  ·  {q.result.parsed?.language?.toUpperCase()}  ·  {q.result.char_count?.toLocaleString()} chars
                        {q.result.ai_cleanup_applied ? '  ·  AI-tell scrub applied' : ''}
                      </p>
                    )}
                    {q.status === 'error' && (
                      <p className="text-[10px] text-rose-600">{q.error}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Coverage matrix */}
          <div className="mt-6 bg-white border border-zinc-200 rounded-xl overflow-hidden" data-testid="arp-coverage">
            <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-2">
              <div>
                <h2 className="text-sm font-bold text-zinc-900">Coverage matrix</h2>
                <p className="text-[11px] text-zinc-500">Green when at least 80 characters of content exist for that language.</p>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <button onClick={() => setFilterLang('all')}
                  className={`text-[10px] px-2 py-1 rounded-full font-bold ${filterLang === 'all' ? 'bg-zinc-950 text-white' : 'bg-zinc-100 text-zinc-700'}`}
                  data-testid="arp-lang-all"
                >ALL</button>
                {SUPPORTED_LANGS.map(l => (
                  <button key={l} onClick={() => setFilterLang(l)}
                    className={`text-[10px] px-2 py-1 rounded-full font-bold uppercase ${filterLang === l ? 'bg-zinc-950 text-white' : 'bg-zinc-100 text-zinc-700'}`}
                    data-testid={`arp-lang-${l}`}
                  >{l}</button>
                ))}
              </div>
            </div>

            {coverage?.summary && (
              <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-200 text-[11px] text-zinc-600 flex flex-wrap gap-3">
                {visibleLangs.map(l => (
                  <span key={l}>
                    <strong className="uppercase">{l}</strong>:  {coverage.summary[l].filled}/{coverage.summary[l].total} ({coverage.summary[l].percent}%)
                    {coverage.summary[l].amber > 0 && (
                      <span className="ml-1 text-amber-700">·  {coverage.summary[l].amber} awaiting review</span>
                    )}
                  </span>
                ))}
              </div>
            )}

            {loading ? (
              <div className="text-center py-10 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
            ) : !coverage ? (
              <div className="text-center py-10 text-zinc-500 text-xs">Could not load coverage.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-[11px]">
                  <thead className="bg-zinc-50">
                    <tr>
                      <th className="text-left px-3 py-2 font-bold text-zinc-700 sticky left-0 bg-zinc-50 z-10">Item</th>
                      <th className="text-left px-3 py-2 font-bold text-zinc-700">Filename</th>
                      {visibleLangs.map(l => (
                        <th key={l} className="px-2 py-2 font-bold text-zinc-700 uppercase text-center">{l}</th>
                      ))}
                      <th className="px-2 py-2 font-bold text-zinc-700 text-center">All</th>
                      <th className="px-2 py-2 font-bold text-rose-700 text-center" title="Delete row (all languages)">Del</th>
                    </tr>
                  </thead>
                  <tbody>
                    {coverage.rows.map(row => (
                      <tr key={row.key} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`arp-row-${row.key}`}>
                        <td className="px-3 py-1.5 sticky left-0 bg-white z-10 font-medium">{row.label}</td>
                        <td className="px-3 py-1.5 font-mono text-zinc-500 text-[10px]">{row.expected_filename}</td>
                        {visibleLangs.map(l => {
                          const state = row.languages[l];
                          const inFlight = translating[`${row.key}-${l}`];
                          const canAutoTranslate = l !== 'en' && row.section !== 'sw' && row.languages.en === 'green' && state === 'empty';
                          return (
                            <td key={l} className="px-2 py-1.5 text-center" data-testid={`arp-cell-${row.key}-${l}`}>
                              {inFlight ? (
                                <Loader2 className="w-4 h-4 text-blue-500 animate-spin inline" />
                              ) : state === 'green' ? (
                                <CheckCircle2 className="w-4 h-4 text-emerald-600 inline" />
                              ) : state === 'amber' ? (
                                <button
                                  onClick={() => verifyOne(row, l)}
                                  title="Machine-translated.  Click to verify (turns green)."
                                  className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-400 hover:bg-amber-500 text-white"
                                  data-testid={`arp-verify-${row.key}-${l}`}
                                >
                                  <ShieldCheck className="w-3 h-3" />
                                </button>
                              ) : canAutoTranslate ? (
                                <button
                                  onClick={() => autoTranslateOne(row, l)}
                                  title={`Auto-translate from EN to ${l.toUpperCase()} (with all 7 safeguards)`}
                                  className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-zinc-300 hover:border-blue-500 hover:bg-blue-50 text-zinc-400 hover:text-blue-600"
                                  data-testid={`arp-autotrans-${row.key}-${l}`}
                                >
                                  <Wand2 className="w-3 h-3" />
                                </button>
                              ) : (
                                <span className="inline-block w-3 h-3 rounded-full border border-zinc-300" />
                              )}
                            </td>
                          );
                        })}
                        <td className="px-2 py-1.5 text-center">
                          {row.section !== 'sw' && row.languages.en === 'green' && SUPPORTED_LANGS.some(l => l !== 'en' && row.languages[l] === 'empty') && (
                            <button
                              onClick={() => autoTranslateRow(row)}
                              title="Translate this row to every empty language"
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-zinc-900 hover:bg-zinc-700 text-white text-[10px] font-bold"
                              data-testid={`arp-bulk-translate-${row.key}`}
                            >
                              <Wand2 className="w-2.5 h-2.5" />
                              ALL
                            </button>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          {SUPPORTED_LANGS.some(l => row.languages[l] !== 'empty') && (
                            <button
                              onClick={() => deleteRow(row, 'all')}
                              disabled={!!deleting[row.key]}
                              title="Delete this row entirely (archives all languages: will ask for confirmation)"
                              className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-rose-200 text-rose-600 hover:bg-rose-50 hover:border-rose-400 disabled:opacity-50"
                              data-testid={`arp-delete-row-${row.key}`}
                            >
                              {deleting[row.key] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="mt-4 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 text-[11px] text-amber-900 leading-relaxed">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-700" />
            <p>
              English uploads automatically run through the AI-tell scrub (em dashes removed, fillers stripped, two spaces after every full stop, one space after every comma).  Translations are stored verbatim.  Re-uploading any file overwrites the previous version for that language slot.
            </p>
          </div>

          {/* Cell legend */}
          <div className="mt-3 flex flex-wrap items-center gap-4 text-[11px] text-zinc-700">
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> human / verified</span>
            <span className="inline-flex items-center gap-1.5"><span className="inline-block w-4 h-4 rounded-full bg-amber-400 flex items-center justify-center"><ShieldCheck className="w-2.5 h-2.5 text-white" /></span> machine-translated, click to verify</span>
            <span className="inline-flex items-center gap-1.5"><span className="inline-block w-4 h-4 rounded-full border border-zinc-300 flex items-center justify-center"><Wand2 className="w-2.5 h-2.5 text-blue-600" /></span> EN ready, click to auto-translate</span>
            <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-full border border-zinc-300" /> empty</span>
          </div>

          {/* Audit modal */}
          {auditOpen && (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setAuditOpen(null)} data-testid="arp-audit-modal">
              <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-lg w-full p-5 max-h-[80vh] overflow-y-auto">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">Translation audit</p>
                    <h3 className="text-base font-bold text-zinc-900">{auditOpen.row.label}  ·  {auditOpen.lang.toUpperCase()}</h3>
                  </div>
                  <button onClick={() => setAuditOpen(null)} className="text-zinc-400 hover:text-zinc-700"><XCircle className="w-5 h-5" /></button>
                </div>
                <div className="grid grid-cols-2 gap-2 text-[11px] text-zinc-700 mb-3">
                  <div className="bg-zinc-50 rounded-lg p-2"><strong>Integrity</strong>:  {auditOpen.audit.integrity ? 'all 7 checks passed' : `${auditOpen.audit.warnings?.length || 0} warning(s)`}</div>
                  <div className="bg-zinc-50 rounded-lg p-2"><strong>Length ratio</strong>:  {(auditOpen.audit.ratio || 0).toFixed?.(2) || auditOpen.audit.ratio}</div>
                  <div className="bg-zinc-50 rounded-lg p-2"><strong>Chunks</strong>:  {auditOpen.audit.chunks}</div>
                  <div className="bg-zinc-50 rounded-lg p-2"><strong>Attempts</strong>:  {auditOpen.audit.attempts}</div>
                </div>
                {auditOpen.audit.warnings?.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-amber-800 mb-1">Warnings</p>
                    <ul className="text-[11px] text-amber-900 leading-relaxed list-disc pl-4 space-y-0.5">
                      {auditOpen.audit.warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  </div>
                )}
                <p className="text-[11px] text-zinc-500 leading-relaxed">
                  The translation has been saved as machine-generated.  Review the rendered cell on the matching reading / lesson page (Library tab) and once you are satisfied, click the amber shield in the matrix to mark it verified.  Audit ID:  <code className="font-mono">{auditOpen.audit.audit_id}</code>
                </p>
              </div>
            </div>
          )}

          {confirmDelete && (() => {
            const { row, language } = confirmDelete;
            const langSuffix = language && language !== 'all'
              ? ` (${language.toUpperCase()} only)` : ' for ALL languages';
            return (
              <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
                   onClick={() => setConfirmDelete(null)}
                   data-testid="confirm-delete-modal">
                <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
                     onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center shrink-0">
                      <AlertTriangle className="w-5 h-5 text-rose-700" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-zinc-900">Am I sure I want to delete this file?</h3>
                      <p className="text-sm text-zinc-700 mt-1">{row.label}{langSuffix}</p>
                      <p className="text-xs text-zinc-500 mt-1">Expected filename: <code className="font-mono">{row.expected_filename}</code></p>
                    </div>
                  </div>
                  <p className="text-xs text-zinc-600 leading-relaxed mb-5">
                    This deletion will be archived to a recoverable backup but the
                    file will disappear from the live coverage matrix immediately.
                  </p>
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setConfirmDelete(null)}
                      className="px-4 py-2 rounded-md bg-zinc-100 hover:bg-zinc-200 text-zinc-800 text-sm font-medium"
                      data-testid="confirm-delete-cancel">
                      Cancel
                    </button>
                    <button
                      onClick={performDelete}
                      className="px-4 py-2 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold inline-flex items-center gap-1.5"
                      data-testid="confirm-delete-confirm">
                      <Trash2 className="w-4 h-4" /> Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </main>
    </div>
  );
}
